import { ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { MechanicsService } from "../../_services/mechanics.service";
import { AuthService } from "src/app/_services/auth.service";
import {
  LayoutConfig,
} from "../../components/app-layout/app-layout.component";
import { SidebarMenuService } from "src/app/_services/sidebar-menu.service";
import { ActivatedRoute, Router } from "@angular/router";
import { DashboardService, EFillingApplication } from "src/app/_services/dashboard.service";
import { ColumnDefinition } from "src/app/components/table/table.component";
import { ConfigurableFilterComponent, FilterConfig, FilterValue } from "src/app/components/configurable-filter/configurable-filter.component";

interface DashboardStat {
  label: string;
  count: number;
  percentChange?: number;
  period?: string;
  color: string;
  icon?: string;
  lastSubmission: string;
}

@Component({
  selector: "app-dashboard",
  standalone: false,
  templateUrl: "./dashboard.component.html",
})
export class DashboardComponent implements OnInit {

  @ViewChild(ConfigurableFilterComponent)
  configurableFilter!: ConfigurableFilterComponent;

  layoutConfig: LayoutConfig;
  breadcrumbItems = [];
  visiblePDF: boolean = false;
  viewTableItem: EFillingApplication;
  switchTabTable: boolean = false;

  dashboardStats: DashboardStat[] = [];
  statsCounts = {
    patents: 24,
    trademarks: 45,
    copyrights: 15,
    designs: 18,
    geographicalIndications: 12,
    postFilings: 12
  }

  searchBar;
  filterConfigs: FilterConfig[] = [
    {
      key: 'active',
      label: 'Active',
      type: 'checkbox',
      section: 'STATUS',
    },
    {
      key: 'inactive',
      label: 'Inactive',
      type: 'checkbox',
      section: 'STATUS',
    },
    {
      key: 'business',
      label: 'Business',
      type: 'checkbox',
      section: 'GROUP TYPE',
    },
    {
      key: 'user',
      label: 'User',
      type: 'checkbox',
      section: 'GROUP TYPE',
    },
    {
      key: 'dropdown',
      label: 'Group Type',
      type: 'dropdown',
      options: [
        {
          label: 'Business',
          value: 'bus',
        },
        {
          label: 'User',
          value: 'user',
        },
      ],
    },
    {
      key: 'createdOnRange',
      label: 'Created Date Range',
      type: 'dateRange',
      placeholder: 'Select date range',
      dateFormat: 'dd/mm/yy',
      section: 'DATE FILTERS',
    },
    {
      key: 'updatedOnRange',
      label: 'Updated Date Range',
      type: 'dateRange',
      placeholder: 'Select date range',
      dateFormat: 'dd/mm/yy',
      section: 'DATE FILTERS',
    },
  ];
  groups: any[] = [];
  filteredGroups: any[] = [];
  appliedFilters: FilterValue[] = [];

  tableColumns: ColumnDefinition[] = [
    { field: 'docId', header: 'File/Doc ID', sortable: true },
    { 
      field: 'logoUrl', 
      header: 'Logo', 
      display: 'avatar',
      size: 'xlarge',
      nameField: 'No Image'
    },
    { field: 'refNumber', header: 'Ref #', sortable: true },
    { field: 'statusDate', header: 'Status Date', sortable: true },
    {
      field: 'status',
      header: 'Status',
      display: 'tag',
      sortable: true,
      severity: (value: string) => {
        const tag = this.getTagColorValue(value);
        return tag?.severity ?? 'secondary';
      },
      value: (value: string) => {
        const tag = this.getTagColorValue(value);
        return tag?.value ?? value;
      },
    },
    {
      field: 'requestSummary',
      header: 'Request Summary',
      sortable: true,
    },
    {
      field: 'filesCount',
      header: 'Affected file(s)',
      sortable: true,
    },
    { field: "invoiceNumber", header: "Invoice #", sortable: false },
    {
      field: 'submittedDate',
      header: 'Submitted',
      sortable: true,
    },
    {
      field: 'submittedBy',
      header: 'Submitted By',
      sortable: true,
    },
    {
      field: 'actions',
      header: 'Actions',
      display: 'actions',
      actions: [
        {
          label: 'Download Payment Details PDF',
          icon: 'pi pi-download',
          action: 'downloadPDP',
          severity: 'info',
        },
        {
          label: 'Download Payment Receipt PDF',
          icon: 'pi pi-download',
          action: 'downloadPRP',
          severity: 'info',
        },
        {
          label: 'View Payment Transactions',
          icon: 'pi pi-eye',
          action: 'viewPaymentTransactions',
          severity: 'info',
        },
      ],
    },
  ];
  globalFilterFields = ['applicationId', 'filingDate', 'grantorName'];

  tableData: EFillingApplication[] = [];
  // Pagination properties
  currentPage = 0;
  pageSize = 10;
  totalRecords = 0;

  constructor(
    private menuService: SidebarMenuService,
    private router: Router,
    private route: ActivatedRoute,
    public ms: MechanicsService,
    private cdr: ChangeDetectorRef,
    public auth: AuthService,
    private dashboardService: DashboardService
  ) {
    this.layoutConfig = {
      headerItems: [{}],
    };
  }

  ngOnInit() {
    // Initialize dashboard data if needed
    const currentPath = this.router.url;
    const menuItems = this.menuService.generateUserManagementMenu(currentPath);
    this.menuService.updateMenuItems(menuItems);

    this.route.params.subscribe((params) => {
      const officeCode =
        params['officeCode'] || this.ms.getCurrentOffice() || 'default';
      const langCode = params['langCode'] || 'en';

      this.breadcrumbItems = [
        {
          label: this.ms.translate('efillingDashboard.title'),
          routerLink: `/${officeCode}/${langCode}/dashboard`,
        },
      ];

      // Trigger change detection after updating breadcrumbs
      this.cdr.markForCheck();
    });

    this.dashboardService.getApplications().subscribe(data => {
      this.tableData = data;
      this.totalRecords = data.length;
    })

    this.initDashboardStats();
  }

  initDashboardStats(): void {
    this.dashboardStats = [
      {
        label: 'Patents',
        count: this.statsCounts.patents,
        color: 'border-blue-400',
        lastSubmission: "10/17/2025"
      },
      {
        label: 'Trademarks',
        count: this.statsCounts.trademarks,
        color: 'border-green-400',
        lastSubmission: "10/30/2025"
      },
      {
        label: 'Copyrights',
        count: this.statsCounts.trademarks,
        color: 'border-green-600',
        lastSubmission: "11/15/2025"
      },
      {
        label: 'Designs',
        count: this.statsCounts.designs,
        color: 'border-red-400',
        lastSubmission: "02/16/2025"
      },
      {
        label: 'Geographical Indications',
        count: this.statsCounts.geographicalIndications,
        color: 'border-yellow-400',
        lastSubmission: "03/22/2025"
      },
      {
        label: 'Post Filings',
        count: this.statsCounts.postFilings,
        color: 'border-pink-400',
        lastSubmission: "01/01/2025"
      },
    ];
  }

  getTagColorValue = (value: string) => {
    switch (value) {
      case 'Under Review':
        return { severity: 'info', value: 'Under Review' };
      case 'Approved':
        return { severity: 'success', value: 'Approved' };
      case 'Pending':
        return { severity: 'warn', value: 'Pending' };
      case 'Objection Raised':
        return { severity: 'warn', value: "Objection Raised" };
      case 'Rejected':
        return { severity: 'danger', value: "Rejected" };
      default:
        return { severity: 'secondary', value }
    }
  };

  onActionClick(action: string, item: EFillingApplication) {
    switch (action) {
      case 'downloadPDP':
        this.onViewTableItem(item);
        break;
      case 'downloadPRP':
        this.onViewTableItem(item);
        break;
      case 'viewPaymentTransactions':
        this.onViewTableItem(item);
        break;
    }
  }

  tabTableSwitch() {
    this.switchTabTable = !this.switchTabTable;
  }

  onViewTableItem(item: EFillingApplication) {
    this.visiblePDF = true;
    this.viewTableItem = item;
    console.info("onViewTableItem viewTableItem: ", this.viewTableItem);
  }

  onLazyLoad(event: any) {
    // Handle lazy loading event from both table and card views
    console.log('Lazy load event:', event);

    // Handle both table and card pagination events
    if (event.first !== undefined && event.rows !== undefined) {
      // Card view pagination
      this.currentPage = Math.floor(event.first / event.rows);
      this.pageSize = event.rows;
    } else if (event.page !== undefined && event.rows !== undefined) {
      // Table view pagination
      this.currentPage = event.page;
      this.pageSize = event.rows;
    }

    console.log(
      'Calculated - currentPage:',
      this.currentPage,
      'pageSize:',
      this.pageSize
    );
  }

  clearAllFilters(): void {
    this.appliedFilters = [];
    this.filteredGroups = this.groups;
    this.configurableFilter.clearAllFilters();
    this.cdr.detectChanges();
  }
  getFilterDisplayValue(filter: FilterValue): string {
    if (filter.key === 'search') {
      return `Search: "${filter.value}"`;
    }
    const filterConfig = this.filterConfigs.find((f) => f.key === filter.key);
    if (!filterConfig) return filter.key;

    switch (filterConfig.type) {
      case 'checkbox':
        return filterConfig.label;
      case 'dateRange':
        if (Array.isArray(filter.value) && filter.value.length === 2) {
          const [startDate, endDate] = filter.value;
          return `${filterConfig.label
            }: ${startDate?.toLocaleDateString()} - ${endDate?.toLocaleDateString()}`;
        }
        return filterConfig.label;
      default:
        return `${filterConfig.label}: ${filter.value}`;
    }
  }
  removeFilterChip(filterKey: string): void {
    // Find the filter config to get the display value
    const filterConfig = this.filterConfigs.find((f) => f.key === filterKey);
    if (filterConfig) {
      // Remove the filter from applied filters
      this.appliedFilters = this.appliedFilters.filter(
        (f) => f.key !== filterKey
      );

      // Also remove the filter from the configurable filter component to sync state
      this.configurableFilter.removeFilterChip(filterKey);

      // Update the filtered groups
      this.applyFilters(this.appliedFilters);
      this.cdr.detectChanges();
    }
  }

  onFilterApplied(filters: FilterValue[]): void {
    console.log('Filters applied:', filters);
    this.applyFilters(filters);
    this.cdr.detectChanges();
  }

  onAppliedFiltersChange(filters: FilterValue[]): void {
    this.appliedFilters = filters;
    this.cdr.detectChanges();
  }

  private applyFilters(filters: FilterValue[]): void {
    let filtered = [...this.groups];

    filters.forEach((filter) => {
      switch (filter.key) {
        case 'active':
          if (filter.value === true) {
            filtered = filtered.filter((item) => item.isActive === true);
          }
          break;
        case 'inactive':
          if (filter.value === true) {
            filtered = filtered.filter((item) => item.isActive === false);
          }
          break;
        case 'business':
          if (filter.value === true) {
            filtered = filtered.filter((item) => item.groupType === 'business');
          }
          break;
        case 'user':
          if (filter.value === true) {
            filtered = filtered.filter((item) => item.groupType === 'user');
          }
          break;
        case 'createdOnRange':
          if (
            filter.value &&
            Array.isArray(filter.value) &&
            filter.value.length === 2
          ) {
            const [startDate, endDate] = filter.value;
            if (startDate && endDate) {
              filtered = filtered.filter((item) => {
                const itemDate = new Date(item.createdOn);
                return itemDate >= startDate && itemDate <= endDate;
              });
            }
          }
          break;
        case 'updatedOnRange':
          if (
            filter.value &&
            Array.isArray(filter.value) &&
            filter.value.length === 2
          ) {
            const [startDate, endDate] = filter.value;
            if (startDate && endDate) {
              filtered = filtered.filter((item) => {
                const itemDate = new Date(item.updatedOn);
                return itemDate >= startDate && itemDate <= endDate;
              });
            }
          }
          break;
      }
    });

    this.filteredGroups = filtered;
  }

  onFilterCleared(): void {
    console.log('Filters cleared');
    this.filteredGroups = this.groups;
    this.cdr.detectChanges();
  }

  onFilterChange(filters: FilterValue[]): void {
    console.log('Filter changed:', filters);
    // Don't apply filters or show red dot on change - only track changes
  }

  getCurrentUser() {
    return this.auth.currentUser$;
  }

  refreshPage() {
    window.location.reload();
  }

  totalStatsCount() {
    return this.statsCounts.copyrights + 
    this.statsCounts.designs + 
    this.statsCounts.geographicalIndications +
    this.statsCounts.patents +
    this.statsCounts.postFilings +
    this.statsCounts.trademarks;
  }

}
